import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { PageContext } from '../../context/PageContext';
import { pages, isOptionSelectionPage, isVisualiserPage } from '../../context/pages';
import { useFlowBridge } from './FlowBridgeContext';
import { useUvInsertsContext } from './UvInsertsContext';
import { resolveBackDestinationThroughCollapsedOptions } from '../navigation/option-tree';

type PageContextShape = {
  currentPage: string;
  goToPage: (page: string) => void;
  pageSelections: Record<string, number>;
  getPageSelection: (pageName: string) => number | null;
  setPageSelection: (pageName: string, index: number | null) => void;
  getPageAllSelected: (pageName: string) => boolean;
  getPageHasSelections: (pageName: string) => boolean;
  resetPageContext: () => void;
};

type UvInsertsNavContextProviderProps = {
  children: React.ReactNode;
};

/**
 * Provides an override of PageContext backed by the TanStack memory router.
 * Header, Footer, and OptionSelectionPage all call usePage() — this context
 * ensures they read router state and navigate via router.navigate() instead of
 * the outer PageContext.setCurrentPage().
 */
export function UvInsertsNavContextProvider({ children }: UvInsertsNavContextProviderProps) {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const { onFinish, onBack } = useFlowBridge();
  const { textByKey, imageByKey, activeConfig, buildCartLineItem, resolved } = useUvInsertsContext();
  const configByHandle = useMemo(() => {
    const map = new Map<string, { active: boolean }>();
    for (const config of resolved?.configs ?? []) {
      map.set(config.handle, { active: config.active });
    }
    return map;
  }, [resolved]);

  // Page selections for option-selection pages (mirrors PageContext.pageSelections).
  const [pageSelections, setPageSelectionsState] = useState<Record<string, number>>({});

  // Derive currentPage from the router location (strip leading '/').
  const rawPathname = routerState.location.pathname;
  const currentPage = (rawPathname.startsWith('/') ? rawPathname.slice(1) : rawPathname) || 'addPersonalTouch';

  const getPageSelection = useCallback((pageName: string): number | null => {
    return pageSelections[pageName] ?? null;
  }, [pageSelections]);

  const setPageSelection = useCallback((pageName: string, index: number | null): void => {
    setPageSelectionsState((prev) => {
      if (index === null) {
        const { [pageName]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [pageName]: index };
    });
  }, []);

  const goToPage = useCallback((destination: string): void => {
    if (destination === 'FINISHED') {
      // Pass the MVVM cart item builder so PageContext can include it in the GIFTWRAP_EVENT.
      // The builder is a closure over current UvInsertsContext state (config, themeIndex, etc.).
      onFinish(buildCartLineItem);
      return;
    }

    if (destination === 'firstPage') {
      onBack();
      return;
    }

    if (destination === 'skipToAddPersonalTouch') {
      navigate({ to: '/addPersonalTouch' });
      return;
    }

    const resolvedDestination = resolveBackDestinationThroughCollapsedOptions({
      currentPage,
      destinationPage: destination,
      configByHandle,
    });

    // Reset option selection state on forward navigation to option-selection pages.
    if (isOptionSelectionPage(resolvedDestination) && resolvedDestination !== 'addPersonalTouch') {
      setPageSelectionsState((prev) => {
        const { [resolvedDestination]: _removed, ...rest } = prev;
        return rest;
      });
    }

    navigate({ to: `/${resolvedDestination}` });
  }, [navigate, onFinish, onBack, buildCartLineItem, currentPage, configByHandle]);

  const getPageHasSelections = useCallback((pageName: string): boolean => {
    if (isOptionSelectionPage(pageName)) {
      return getPageSelection(pageName) !== null;
    }

    if (isVisualiserPage(pageName)) {
      // For visualiser pages, check MVVM state rather than legacy requiredUserInput keys.
      if (!activeConfig) return false;

      const hasText = Object.values(textByKey).some(
        (v) => typeof v === 'string' && v.trim() !== ''
      );
      if (hasText) return true;

      const hasImage = Object.values(imageByKey).some((f) => f instanceof File);
      return hasImage;
    }

    // Default: pass if we have any personalisation choices (gift wrap selection).
    return false;
  }, [getPageSelection, textByKey, imageByKey, activeConfig]);

  const getPageAllSelected = useCallback((pageName: string): boolean => {
    return getPageHasSelections(pageName);
  }, [getPageHasSelections]);

  const resetPageContext = useCallback((): void => {
    setPageSelectionsState({});
  }, []);

  const value = useMemo<PageContextShape>(() => ({
    currentPage,
    goToPage,
    pageSelections,
    getPageSelection,
    setPageSelection,
    getPageAllSelected,
    getPageHasSelections,
    resetPageContext,
  }), [
    currentPage,
    goToPage,
    pageSelections,
    getPageSelection,
    setPageSelection,
    getPageAllSelected,
    getPageHasSelections,
    resetPageContext,
  ]);

  // Override PageContext with router-backed values for Header, Footer, and OptionSelectionPage.
  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
}
